import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import type {MixSockets} from "#material/nodes/converter/mix/definition"
import {mixDefinition} from "#material/nodes/converter/mix/definition"
import type {RgbBlendType} from "#material/three/three-utils"
import {getColor, threeConvert, threeRGBColor, threeValue, threeVec3} from "#material/three/three-utils"
import {registerMaterialGraphNode} from "#material/register"
import {ThreeTsl as TSL} from "#material/three/exports"
import type {ThreeReturns} from "#material/sockets/three"
import type {CyclesReturns} from "#material/sockets/cycles"
import {cyclesColor, cyclesVec3} from "#material/cycles/helpers"
import {cyclesMixType} from "#material/nodes/color/mix-rgb/helpers"
import type {MixColorNode, MixFloatNode, MixVectorNode, MixVectorNonUniformNode} from "#material/cycles/types"

@registerMaterialGraphNode
export class Mix extends MaterialNode<MixSockets> {
    public override get definition() {
        return mixDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<MixSockets>> => {
        const {blendType, clampFactor, clampResult, dataType} = this.parameters.settings
        const factorFloat = await evaluator.get(this.parameters.inputs.factorFloat)

        let facNode = factorFloat?.toFloat() ?? threeConvert(this.parameters.settings.factorFloat, threeValue) ?? threeValue(1)
        if (clampFactor) {
            facNode = TSL.clamp(facNode, threeValue(0), threeValue(1))
        }

        switch (dataType ?? "RGBA") {
            case "RGBA": {
                const {aColor, bColor} = await evaluator.getAll({aColor: this.parameters.inputs.aColor, bColor: this.parameters.inputs.bColor})
                const inputA = aColor?.toColor() ?? threeConvert(this.parameters.settings.aColor, threeRGBColor) ?? threeRGBColor({r: 0, g: 0, b: 0})
                const inputB = bColor?.toColor() ?? threeConvert(this.parameters.settings.bColor, threeRGBColor) ?? threeRGBColor({r: 0, g: 0, b: 0})
                const mixedColor = getColor(blendType ?? "MIX", inputA, inputB, facNode)
                return {resultColor: clampResult ? TSL.color(TSL.clamp(mixedColor.rgb, threeValue(0), threeValue(1))) : mixedColor}
            }
            case "FLOAT": {
                const {aFloat, bFloat} = await evaluator.getAll({aFloat: this.parameters.inputs.aFloat, bFloat: this.parameters.inputs.bFloat})
                const inputA = aFloat?.toFloat() ?? threeConvert(this.parameters.settings.aFloat, threeValue) ?? threeValue(0)
                const inputB = bFloat?.toFloat() ?? threeConvert(this.parameters.settings.bFloat, threeValue) ?? threeValue(0)
                return {resultFloat: TSL.mix(inputA, inputB, facNode)}
            }
            case "VECTOR": {
                const {aVector, bVector} = await evaluator.getAll({aVector: this.parameters.inputs.aVector, bVector: this.parameters.inputs.bVector})
                const inputA = aVector?.toVec3() ?? threeConvert(this.parameters.settings.aVector, threeVec3) ?? threeVec3({x: 0, y: 0, z: 0})
                const inputB = bVector?.toVec3() ?? threeConvert(this.parameters.settings.bVector, threeVec3) ?? threeVec3({x: 0, y: 0, z: 0})
                return {resultVector: TSL.mix(inputA, inputB, facNode)}
            }
            default:
                throw new Error(`Mix node data type ${dataType} is not supported`)
        }
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<MixSockets>> => {
        const {clampFactor, clampResult, dataType} = this.parameters.settings
        const fac = await evaluator.get(this.parameters.inputs.factorFloat)

        switch (dataType ?? "RGBA") {
            case "RGBA": {
                const mixNode: MixColorNode = {
                    type: "mix_color",
                    inputs: {
                        ...(await evaluator.getAll({a: this.parameters.inputs.aColor, b: this.parameters.inputs.bColor})),
                        fac,
                    },
                    parameters: {
                        a: cyclesColor(this.parameters.settings.aColor),
                        b: cyclesColor(this.parameters.settings.bColor),
                        fac: this.parameters.settings.factorFloat,
                        blend_type: cyclesMixType(this.parameters.settings.blendType as RgbBlendType | undefined),
                        use_clamp: clampFactor,
                        use_clamp_result: clampResult,
                    },
                    resources: {},
                }
                return {resultColor: [mixNode, "result"]}
            }
            case "FLOAT": {
                const mixNode: MixFloatNode = {
                    type: "mix_float",
                    inputs: {
                        ...(await evaluator.getAll({a: this.parameters.inputs.aFloat, b: this.parameters.inputs.bFloat})),
                        fac,
                    },
                    parameters: {
                        a: this.parameters.settings.aFloat,
                        b: this.parameters.settings.bFloat,
                        fac: this.parameters.settings.factorFloat,
                        use_clamp: clampFactor,
                    },
                    resources: {},
                }
                return {resultFloat: [mixNode, "result"]}
            }
            case "VECTOR": {
                const operands = await evaluator.getAll({a: this.parameters.inputs.aVector, b: this.parameters.inputs.bVector})
                if (this.parameters.settings.factorMode === "NON_UNIFORM") {
                    const mixNode: MixVectorNonUniformNode = {
                        type: "mix_vector_non_uniform",
                        inputs: {...operands, fac: await evaluator.get(this.parameters.inputs.factorVector)},
                        parameters: {
                            a: cyclesVec3(this.parameters.settings.aVector),
                            b: cyclesVec3(this.parameters.settings.bVector),
                            fac: cyclesVec3(this.parameters.settings.factorVector),
                            use_clamp: clampFactor,
                        },
                        resources: {},
                    }
                    return {resultVector: [mixNode, "result"]}
                }
                const mixNode: MixVectorNode = {
                    type: "mix_vector",
                    inputs: {...operands, fac},
                    parameters: {
                        a: cyclesVec3(this.parameters.settings.aVector),
                        b: cyclesVec3(this.parameters.settings.bVector),
                        fac: this.parameters.settings.factorFloat,
                        use_clamp: clampFactor,
                    },
                    resources: {},
                }
                return {resultVector: [mixNode, "result"]}
            }
            default:
                throw new Error(`Mix node data type ${dataType} is not supported`)
        }
    }
}
